import * as d3 from 'd3';
import {
  scaled,
  adjustProjection,
  intermediateValue,
  csvToMap
} from '../utils';
import { casesColorInterpolator, deathsColorInterpolator } from '../utils/project-utils';
import choroplethLegend from '../utils/choropleth-legend';
import * as topojson from 'topojson-client';
import { debounce } from 'underscore';
import {init} from 'svelte/internal';
import dayjs from 'dayjs';

export default function() {
  // required parameters
  let mapData;
  let data;
  let dataType;

  // optional parameters
  let chartId = 'europe-map';
  let isAnimating = false;
  let dataIndex;
  let updateDataIndex; // function to update dataIndex in the parent component

  // constants
  const height = scaled(600, width);
  const padding = [0, 10, 40, 10];
  const internalBoundaryStrokeValue = 0.6; // between 0 and 1
  let initialised = false;
  const dateIndicator = {height: 10, padding: [10, 10]};

  let colorScale, mapGroup, zoomGroup, choroplethLegendInstance, countries, valueAccessor, colorInterpolator, dataById,
    internalBoundaryStrokeColor, dateScale, svg, isDraggingDateIndicator, intervalId, dataIndicesByDate;

  const getValue = function(d, property) {
    const row = dataById.get(d.properties.ISO3_CODE);
    if (row != null) {
      return row[property];
    } else {
      return 0;
    }
  };

  const dataTypeSpecific = function(dataType) {
    switch (dataType) {
      case 'totalCases':
        return [d => getValue(d, 'total_cases_per_million'), casesColorInterpolator()];

      case 'newCases':
        return [d => getValue(d, 'new_cases_per_million'), casesColorInterpolator()];

      case 'totalDeaths':
        return [d => getValue(d, 'total_deaths_per_million'), deathsColorInterpolator()];

      case 'newDeaths':
        return [d =>  getValue(d, 'new_deaths_per_million'), deathsColorInterpolator()];
    }
  };

  const update = function() {
    if (initialised) {
      [valueAccessor, colorInterpolator] = dataTypeSpecific(dataType);
      const extent = d3.extent(countries.features, valueAccessor);
      colorScale.interpolator(colorInterpolator).domain(extent);
      internalBoundaryStrokeColor = colorScale(intermediateValue(internalBoundaryStrokeValue, extent));
      const duration = 150;

      // fill
      mapGroup.selectAll('.countries > path')
        .transition()
        .duration(duration)
        .attr('fill', d => colorScale(valueAccessor(d)));

      // boundary stroke
      mapGroup.selectAll('.internal-boundaries > path')
        .transition()
        .duration(duration)
        .attr('stroke', internalBoundaryStrokeColor);

      // legend
      zoomGroup.call(choroplethLegendInstance
        .colorScale(colorScale)
        .valueAccessor(valueAccessor));
    }
  };

  const dateAsKey = function(date) {
    return dayjs(date).format('YYYY-MM-DD');
  };

  const chart = function(selection) {
    selection.each(function () {
      const container = d3.select(this);

      [valueAccessor, colorInterpolator] = dataTypeSpecific(dataType);

      countries = topojson.feature(mapData, mapData.objects.countries);
      const internalBoundaries = topojson.feature(mapData, mapData.objects.internalBoundaries);
      const projection = d3.geoMercator();
      const path = d3.geoPath(projection);
      const mapSize = [width - (padding[1] + padding[3]), height - (padding[0] + padding[2])];
      const [mapScale, _, mapBounds] = adjustProjection(projection, path, countries, {
        size: mapSize,
        origin: [padding[3], padding[0]]
      });

      const extent = d3.extent(countries.features, valueAccessor);
      colorScale = d3.scaleSequential(colorInterpolator).domain(extent);
      internalBoundaryStrokeColor = colorScale(intermediateValue(internalBoundaryStrokeValue, extent));

      dateScale = d3.scaleTime()
        .domain(d3.extent(data.map(d => d[0])))
        .range([0, width - (padding[1] + padding[3])]);

      let timer, transform;

      const zoom = d3.zoom()
        .on('zoom', function (e) {
          zoomGroup.attr('transform', (transform = e.transform))
            .select('.internal-boundary')
            .attr('stroke-width', 1 / e.transform.k)
            .attr('stroke-dasharray', `${2 / e.transform.k} ${2 / e.transform.k}`)
        });

      container.attr('id', chartId)
        .attr('class', 'chart map');

      const tooltip = container.append('div')
        .attr('class', 'tooltip');

      svg = container.append('svg')
        .attr('viewBox', `0 0 ${width} ${height}`)
        .attr('preserveAspectRatio', 'xMinYMin meet')
        .on('mousemove', function(event) {
          if (isDraggingDateIndicator) {
            const [x] = d3.pointer(event);
            if (x >= padding[3] && x <= width - padding[1]) {
              d3.select('.date-indicator')
                .attr('transform', `translate(${x - dateIndicator.padding[0]} ${dateIndicator.y})`);
              debounce(function() {
                chart.date(dateScale.invert(x - padding[3]))
              }, 250)();
            }
          }
        })
        .on('mouseup', function(event, d) {
          isDraggingDateIndicator = false;
        });
        //.call(zoom)

      zoomGroup = svg.append('g')
        .attr('class', 'zoom');

      mapGroup = zoomGroup.append('g')
        .attr('class', 'map-group');

      // countries
      let g = mapGroup.append('g')
        .attr('class', 'countries');

      g.selectAll('path')
        .data(countries.features)
        .enter()
        .append('path')
        .attr('d', path)
        .attr('class', 'area')
        .on('mouseover', function (event, d) {
          if (timer) {
            window.clearTimeout(timer);
          }
          const position = d3.pointer(event, container.node())
          tooltip.attr('style', `display:block; left: ${position[0] + 10}px; top: ${position[1]}px`);
          tooltip.node().innerHTML = `
        <div class='wrapper'>
          <div class='location'>${d.properties.NAME_ENGL}</div>
          <div class='value'>${valueAccessor(d) !== 0 ? `${d3.format(',.0f')(valueAccessor(d))}/million` : 'No data'}</div>
        </div>`;
          d3.select(this).attr('fill', d => d3.rgb(colorScale(valueAccessor(d))).darker(0.3).toString());
        })
        .on('mouseout', function () {
          d3.select(this).attr('fill', d => colorScale(valueAccessor(d)));
          timer = window.setTimeout(function () {
            tooltip.attr('style', 'display:none')
          }, 300)
        })
        .attr('fill', d => colorScale(valueAccessor(d)));

      // internal boundaries
      mapGroup.append('g')
        .attr('class', 'internal-boundaries')
        .selectAll('path')
        .data(internalBoundaries.features)
        .enter()
        .append('path')
        .attr('class', 'internal-boundary')
        .attr('stroke', internalBoundaryStrokeColor)
        .attr('stroke-dasharray', '2 2')
        .attr('d', path);

      // x-axis
      svg.append('g')
        .attr('class', 'axis date-axis')
        .attr('transform', `translate(${padding[3]} ${height - padding[2] + 17})`)
        .call(d3.axisBottom(dateScale));

      // date indicator
      const x = dateScale(chart.date()) + padding[3];
      dateIndicator.y = height - padding[2] + 5 - (dateIndicator.padding[1]);

      g = svg.append('g')
        .attr('class', 'date-indicator is-enabled')
        .attr('transform', `translate(${x - dateIndicator.padding[0]} ${dateIndicator.y})`)
        .on('mousedown', function() {
          if (!isAnimating) {
            isDraggingDateIndicator = true;
          }
        });

      g.append('rect')
        .attr('x', 0)
        .attr('y', 0)
        .attr('width', (dateIndicator.padding[0] * 2))
        .attr('height', dateIndicator.height + (dateIndicator.padding[1] * 2))
        .attr('fill', '#fff')
        .attr('fill-opacity', 0);

      g.append('line')
        .attr('x1', dateIndicator.padding[0])
        .attr('y1', dateIndicator.padding[1])
        .attr('x2', dateIndicator.padding[0])
        .attr('y2', dateIndicator.padding[1] + dateIndicator.height);

      // legend
      choroplethLegendInstance = choroplethLegend()
        .chartId(chartId)
        .svgWidth(width)
        .svgHeight(height)
        .colorScale(colorScale)
        .valueAccessor(valueAccessor)
        .features(countries.features)
        .x(mapBounds[1][0] + 50)
        .transitionDuration(200);

      zoomGroup.call(choroplethLegendInstance);

      initialised = true;
    });
  };

  chart.mapData = function(_) {
    if (!arguments.length) return mapData;
    mapData = _;
    return chart;
  };

  chart.data = function(_) {
    if (!arguments.length) return data;
    data = _;
    dataIndex = data.length - 1;
    dataById = csvToMap(data[dataIndex][1], 'iso_code');
    dataIndicesByDate = new Map();
    data.forEach((d, i) => {
      dataIndicesByDate.set(dateAsKey(d[0]), i);
    });
    return chart;
  };

  chart.date = function (_) {
    if (!arguments.length) {
      return data[dataIndex][0];
    }
    chart.dataIndex(dataIndicesByDate.get(dateAsKey(_)));
    return chart;
  };

  chart.dataType = function(_) {
    if (!arguments.length) return dataType;
    dataType = _;
    update();
    return chart;
  };

  chart.chartId = function(_) {
    if (!arguments.length) return chartId;
    chartId = _;
    return chart;
  };

  chart.isAnimating = function (_) {
    if (!arguments.length) return isAnimating;
    isAnimating = _;
    const sel = d3.select('.date-indicator');
    if (isAnimating) {
      sel.attr('class', 'date-indicator');
      intervalId = setInterval(() => {
        if (dataIndex < data.length - 1) {
          chart.dataIndex(dataIndex + 1);
        } else {
          dataIndex = 0;
        }
      }, 100);
    } else {
      sel.attr('class', 'date-indicator is-enabled');
      clearInterval(intervalId);
    }
    return chart;
  };

  chart.dataIndex = function (_) {
    if (!arguments.length) return dataIndex;
    dataIndex = _;
    if (initialised) {
      const dataForDate = data[dataIndex];
      dataById = csvToMap(dataForDate[1], 'iso_code');

      if (!isDraggingDateIndicator) {
        const x = dateScale(dataForDate[0]) + padding[3];
        svg.select('.date-indicator')
          .transition()
          .duration(100)
          .attr('transform', `translate(${x - dateIndicator.padding[0]} ${dateIndicator.y})`);
      }

      if (updateDataIndex) {
        updateDataIndex(dataIndex);
      }

      update();
    }
    return chart;
  };

  chart.updateDataIndex = function (_) {
    if (!arguments.length) return updateDataIndex;
    updateDataIndex = _;
    return chart;
  };

  return chart;
};
